import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createHomeHQCheckout,
  createHomeHQPortalSession,
  getHomeHQAccess,
  getHomeHQPlans,
  startHomeHQPurchaseClaim,
  verifyHomeHQPurchaseClaim,
} from '@/services/api/billing';

export const HOMEHQ_ACCESS_QUERY_KEY = ['homehq-access'];

/**
 * The caller's HomeHQ status and limits. `pollWhileLocked` re-checks every 2s
 * while access is still off — used right after Stripe Checkout, before the
 * webhook has recorded the new subscription.
 */
export const useHomeHQAccess = (options: { enabled?: boolean; pollWhileLocked?: boolean } = {}) =>
  useQuery({
    queryKey: HOMEHQ_ACCESS_QUERY_KEY,
    queryFn: getHomeHQAccess,
    enabled: options.enabled ?? true,
    staleTime: 30 * 1000,
    refetchInterval: (query) =>
      options.pollWhileLocked && query.state.data && !query.state.data.hasAccess ? 2000 : false,
  });

export const useHomeHQPlans = (enabled = true) =>
  useQuery({
    queryKey: ['homehq-plans'],
    queryFn: getHomeHQPlans,
    enabled,
    staleTime: 5 * 60 * 1000,
  });

/** Leaves the app for Stripe, so success = navigation; only errors come back. */
export const useStartHomeHQCheckout = () =>
  useMutation({
    mutationFn: async (data: { lookupKey: string; promoCode?: string }) => {
      window.location.assign(await createHomeHQCheckout(data));
    },
  });

export const useOpenHomeHQBillingPortal = () =>
  useMutation({
    mutationFn: async () => {
      window.location.assign(await createHomeHQPortalSession());
    },
  });

export const useStartHomeHQPurchaseClaim = () =>
  useMutation({ mutationFn: startHomeHQPurchaseClaim });

/** On success the new access lands in the cache, so the paywall gives way to HomeHQ. */
export const useVerifyHomeHQPurchaseClaim = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: verifyHomeHQPurchaseClaim,
    onSuccess: (access) => queryClient.setQueryData(HOMEHQ_ACCESS_QUERY_KEY, access),
  });
};
